import { Socket } from 'socket.io-client';

export type ByteBuddiesSocket = Socket<ServerToClientEvents>;

/**
 * Represents a User in the application.
 */
export interface User {
  _id?: string;
  username: string;
  profilePicture: string;
  followers: User[];
  following: User[];
  achievements: Achievement[];
  awards: string[];
  score: number;
  notifications: Notification[];
  game: (string | number)[][];
  questions: Question[];
  answers: Answer[];
  comments: Comment[];
}

export interface UserData extends User {
  password: string;
  role: string;
}

/**
 * Enum representing the possible ordering options for questions.
 * and their display names.
 */
export const orderTypeDisplayName = {
  newest: 'Newest',
  unanswered: 'Unanswered',
  active: 'Active',
  mostViewed: 'Most Viewed',
} as const;

/**
 * Type representing the keys of the orderTypeDisplayName object.
 * This type can be used to restrict values to the defined order types.
 */
export type OrderType = keyof typeof orderTypeDisplayName;

/**
 * Interface represents a comment.
 *
 * _id - The unique identifier for the comment.
 * text - The text of the comment.
 * commentBy - Username of the author of the comment.
 * commentDateTime - Time at which the comment was created.
 */
export interface Comment {
  _id?: string;
  text: string;
  commentByUser: User;
  commentDateTime: Date;
}

/**
 * Interface representing a tag associated with a question.
 *
 * @property name - The name of the tag.
 * @property description - A description of the tag.
 */
export interface Tag {
  _id?: string;
  name: string;
  description: string;
}

/**
 * Interface representing a tag associated with a question with a question count.
 *
 * @property name - The name of the tag.
 * @property description - A description of the tag.
 * @property qcnt - The number of questions associated with the tag.
 */
export interface TagWithQcnt {
  _id?: string;
  name: string;
  description: string;
  qcnt: number;
}

/**
 * Interface represents the data for a tag.
 *
 * name - The name of the tag.
 * qcnt - The number of questions associated with the tag.
 */
export interface TagData {
  name: string;
  qcnt: number;
}

/**
 * Interface representing the voting data for a question, which contains:
 * - qid - The ID of the question being voted on
 * - upVotes - An array of user IDs who upvoted the question
 * - downVotes - An array of user IDs who downvoted the question
 */
export interface VoteData {
  qid: string;
  upVotes: string[];
  downVotes: string[];
}

/**
 * Interface representing an Answer document, which contains:
 * - _id - The unique identifier for the answer. Optional field
 * - text - The content of the answer
 * - ansBy - The username of the user who wrote the answer
 * - ansDateTime - The date and time when the answer was created
 * - comments - Comments associated with the answer.
 */
export interface Answer {
  _id?: string;
  text: string;
  ansByUser: User;
  ansDateTime: Date;
  comments: Comment[];
}

/**
 * Interface representing an Answer document with its QID.
 * Extends Answer.
 *
 * - qid - The unique identifier for the question the answer is part of
 */
export interface AnswerWithQid extends Answer {
  qid?: string;
}

/**
 * Interface representing the structure of a Answer object with a score attribute.
 * Extends AnswerWithQid
 *
 * - score - The score of the answer.
 */
export interface AnswerWithScore extends AnswerWithQid {
  score: number;
}

/**
 * Interface representing the structure of a Question object.
 *
 * - _id - The unique identifier for the question.
 * - tags - An array of tags associated with the question, each containing a name and description.
 * - answers - An array of answers to the question
 * - title - The title of the question.
 * - views - An array of usernames who viewed the question.
 * - text - The content of the question.
 * - askedBy - The username of the user who asked the question.
 * - askDateTime - The date and time when the question was asked.
 * - upVotes - An array of usernames who upvoted the question.
 * - downVotes - An array of usernames who downvoted the question.
 * - comments - Comments associated with the question.
 */
export interface Question {
  _id?: string;
  tags: Tag[];
  answers: Answer[];
  title: string;
  views: string[];
  text: string;
  askedByUser: User;
  askDateTime: Date;
  upVotes: string[];
  downVotes: string[];
  comments: Comment[];
}

/**
 * Interface representing the structure of a Question object with a score attribute.
 * Extends Question
 *
 * - score - The score of the question.
 */
export interface QuestionWithScore extends Question {
  score: number;
}

/**
 * Interface representing the payload for a vote update socket event.
 */
export interface VoteUpdatePayload {
  qid: string;
  upVotes: string[];
  downVotes: string[];
}

export interface AnswerUpdatePayload {
  qid: string;
  answer: Answer;
}

export interface CommentUpdatePayload {
  result: Question | Answer;
  type: 'question' | 'answer';
}

export interface GameUpdatePayload {
  waiting: User | undefined;
  result: (string | number[])[];
}

/**
 * Interface representing the possible events that the server can emit to the client.
 */
export interface ServerToClientEvents {
  questionUpdate: (question: Question) => void;
  answerUpdate: (update: AnswerUpdatePayload) => void;
  viewsUpdate: (question: Question) => void;
  voteUpdate: (vote: VoteUpdatePayload) => void;
  commentUpdate: (update: CommentUpdatePayload) => void;
  userNotificationUpdate: (user: User) => void;
  followUpdate: (update: string) => void;
  gameUpdate: (update: GameUpdatePayload) => void;
  userUpdate: (uid: string) => void;
}

/**
 * Enum representing the possible icon types for notifications.
 */
export enum NotificationType {
  Upvote = 'upvote',
  Downvote = 'downvote',
  Comment = 'comment',
  Question = 'question',
  Answer = 'answer',
  Follow = 'follow',
  Award = 'award',
  Achievement = 'achievement',
  Reply = 'reply',
}

/**
 * Interface representing the possible events that the client can emit to the server.
 *
 * - id: The ID of the notification
 * - message: The message to be sent
 * - sender: Who caused the notification. For example, the person who upvoted the question.
 * - receiver: Who does the notification effect: For example, the person whose question was upvoted.
 * - date: The date and time when the notification was created.
 * - path: The path to the question or answer that the notification is about.
 */
export interface Notification {
  _id?: string;
  message: string;
  sender: User;
  receiver: User;
  notificationType: NotificationType;
  notificationDateTime: Date;
  path: string;
}

/**
 * Interface representing an achievement.
 * - id: The unique identifier for the achievement.
 * - name: The name of the achievement.
 * - description: The description of the achievement.
 * - icon: The URL of the achievement's icon.
 * - goal: The goal for earning the achievement.
 * - progress: The user's progress towards the goal.
 */
export interface Achievement {
  _id?: string;
  name: string;
  description: string;
  icon: string;
  goal: number;
  progress: number;
  points: number;
  awarded: boolean;
}

/**
 * Interface representing an award.
 * - id: The unique identifier for the award.
 * - name: The name of the award.
 * - description: The description of the award.
 * - icon: The URL of the award's icon.
 * - points: The number of points associated with the award.
 */
export interface Award {
  _id?: string;
  name: string;
  description: string;
  icon: string;
  points: number;
}

/**
 * Interface representing an awarded award.
 * - award: The key of the award that was awarded.
 * - count: The number of times the award was awarded.
 */
export interface AwardedAward {
  award: string;
  count: number;
}

/**
 * Interface extending the request body when adding a reward to a user, which contains:
 *
 * - uid - The unique identifier of the user receiving the reward.
 * - reward - The reward being added to the user.
 * - type - The type of the reward, either 'award' or 'achievement'.
 */
export interface RewardEventRequestBody {
  uid: string;
  reward: string;
  type: 'award' | 'achievement';
}
